<script setup>
import { computed } from "vue";
import { breakpointsBootstrapV5, useBreakpoints } from "@vueuse/core";

import { ButtonSeverity, TuBasePopup, TuButton } from "@tinyurldev/tu-ui-vue";
import { startCase } from "lodash-es";
import useTranslation from "@Composables/useTranslation";

const emits = defineEmits(["close"]);

const props = defineProps({
  parsedContent: {
    type: String,
    required: true,
  },

  buttonLabel: {
    type: String,
    required: true,
  },

  ctaRoute: {
    type: String,
    required: true,
  },

  /**
   * A callback that you can use to run arbitrary code when the popup is closed. The Original purpose for this
   * was to mark the notification as "read" so that the popup does not keep appearing every time the user
   * refreshes the page
   */
  onClose: {
    type: Function,
    default: () => ({}),
  },
});

const { smallerOrEqual } = useBreakpoints(breakpointsBootstrapV5);
const { __ } = useTranslation();

const isMobileView = smallerOrEqual("xl");

const titleWithoutContent = computed(() => {
  const match = props.parsedContent.match(/<p><strong>(.*?)<\/strong><\/p>/);

  return match ? match[1] : "";
});

const contentWithoutTitle = computed(() => {
  return props.parsedContent.replace(/<p><strong>.*?<\/strong><\/p>/, "").trim();
});

function closeModal() {
  props.onClose();

  emits("close");
}
</script>

<template>
  <FocusTrap>
    <TuBasePopup
      id="notification-popup"
      tabindex="-1"
      class="notification-popup _notif_popup"
      is-closeable
      @close="closeModal"
    >
      <template #title>
        {{ titleWithoutContent }}
      </template>
      <template #content>
        <!-- eslint-disable vue/no-v-html -->
        <div
          class="notification-popup__content"
          v-html="contentWithoutTitle"
        />
      </template>
      <template #footer>
        <div class="notification-popup__buttons">
          <TuButton
            v-if="!isMobileView"
            class="_notif_popup_close"
            :label="startCase(__('close'))"
            is-outlined
            :severity="ButtonSeverity.CANCEL"
            @click="closeModal"
          />
          <TuButton
            as="a"
            :href="props.ctaRoute"
            :is-block="isMobileView"
            :label="buttonLabel"
            @click="closeModal"
          />
        </div>
      </template>
    </TuBasePopup>
  </FocusTrap>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/typography.scss" as typography;
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;

.notification-popup {
  width: 100%;
  max-width: none;

  &__content {
    font-size: typography.$base-font-size;
    line-height: typography.$base-line-height;
  }

  &__buttons {
    display: flex;
    justify-content: flex-end;
    align-items: end;
    gap: spacing.$xxs;
  }
}
</style>
